import { showNotification } from '@mantine/notifications';
import { useCallback, useContext } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { reportEvent } from '~/domains/analytics';
import { DeepLinkType } from '~/domains/analytics/deepLinks/types';
import { useSignupDeepLinkWebToApp } from '~/domains/analytics/deepLinks/useDeepLink';
import { Routes } from '~/domains/common/constants/routes';

export const useCopyReferralCode = ({
  referralCode,
  onClose,
}: {
  referralCode: string;
  onClose: () => void;
}) => {
  const [deepLink] = useSignupDeepLinkWebToApp(referralCode, DeepLinkType.REFER_A_FRIEND);

  const { user } = useContext(UserContext);
  // copies referral code to clipboard
  const handleCopy = useCallback(
    (value?: string) => {
      window.navigator.clipboard.writeText(value ?? referralCode);

      showNotification({
        message: `Referral code copied!`,
        color: 'green',
        autoClose: 2500,
      });

      reportEvent('RAF > Copy code', {
        raf_code: user?.referralCode,
      });
    },
    [referralCode, user?.referralCode]
  );

  // tries to share referral code via native share API, falls back to copying to clipboard
  const handleCopyLink = useCallback(async () => {
    const referralURL =
      deepLink as unknown as string ??
      `${window.location.origin}${Routes.signUp({ referralCode })}`;

    try {
      if (
        /(iPad)|(iPhone)|(iPod)|(android)|(webOS)/iu.exec(navigator.userAgent) &&
        navigator.share
      ) {
        await navigator.share({ title: 'Sign up with my referral code!', url: referralURL });

        reportEvent('RAF > Share code', {
          raf_code: user?.referralCode,
        });
      } else {
        handleCopy(referralURL);
      }

      onClose();
    } catch (err) {
      // Native share throws "AbortError" type when users dismisses the share modal
      // We don't want to show an error for that
      if ((err as Error)?.name !== 'AbortError') {
        showNotification({
          message: `Could not copy URL: ${referralURL}.`,
          color: 'red',
          autoClose: 2500,
        });
      }
    }
  }, [deepLink, handleCopy, onClose, referralCode, user?.referralCode]);

  return { handleCopy, handleCopyLink };
};
